import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasRole } from '@3dera/core/services/auth';
import { getTotalUnreadCount } from '@3dera/core/services/messages';
import { useLanguage } from '../../context/LanguageContext';
import styles from './BottomTabBar.module.css';

const BottomTabBar = () => {
  const location = useLocation();
  const { currentUser, userData, isAuthenticated, myStoreId } = useAuth();
  const isAdmin = userData?.roles && hasRole(userData.roles, 'admin');
  const isModerator = userData?.roles && hasRole(userData.roles, 'moderator');
  const isSeller = userData?.roles && hasRole(userData.roles, 'seller');
  const showPanel = isAdmin || isModerator || isSeller;
  const { t } = useLanguage();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    const unsub = getTotalUnreadCount(currentUser.uid, myStoreId, setUnreadMessages);
    return () => unsub();
  }, [currentUser, myStoreId]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    // Panel: hem /admin hem /seller-dashboard kontrol et
    if (path === '/admin' || path === '/seller-dashboard') {
      return location.pathname.startsWith('/admin') || location.pathname.startsWith('/seller-dashboard');
    }
    return location.pathname.startsWith(path);
  };

  const tabs = [
    {
      id: 'home',
      path: '/',
      label: t('navbar.home') || 'Ana Sayfa',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      id: 'marketplace',
      path: '/marketplace',
      label: t('navbar.tabGallery') || 'Galeri',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
    },
    // Mesajlar sadece giriş yapmış kullanıcılarda
    ...(isAuthenticated ? [{
      id: 'messages',
      path: '/mesajlar',
      label: t('navbar.messages') || 'Mesajlar',
      badge: unreadMessages,
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    }] : []),
    // Panel: Admin/Moderator → /admin, Satıcı → /seller-dashboard
    ...(showPanel ? [{
      id: 'panel',
      path: isAdmin || isModerator ? '/admin' : '/seller-dashboard',
      label: isAdmin ? 'Admin' : isModerator ? 'Moderator' : 'Mağaza',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    }] : []),
    {
      id: 'profile',
      path: isAuthenticated ? '/profile' : '/auth',
      label: isAuthenticated ? (t('navbar.tabProfile') || 'Profil') : (t('navbar.signIn') || 'Giriş'),
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
  ];

  return (
    <nav className={styles.tabBar}>
      {tabs.map((tab) => (
        <Link
          key={tab.id}
          to={tab.path}
          className={`${styles.tab} ${isActive(tab.path) ? styles.active : ''}`}
        >
          <span className={styles.iconWrap}>
            {tab.icon}
            {tab.badge > 0 && (
              <span className={styles.badge}>
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            )}
          </span>
          <span className={styles.label}>{tab.label}</span>
        </Link>
      ))}
    </nav>
  );
};

export default BottomTabBar;
